
import Vue, { PropType } from 'vue'
import Uppy from '@uppy/core'
import Dashboard from '@uppy/dashboard'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import { uppyCompanionUrl } from '@/config'
import { mapGetters } from 'vuex'
import { indicateNth } from '@/services/files'

// Uppy styles for plugins
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import { trackDriveBrowserFilesUploadedAttempt } from '@/services/tracking'
import { FileMetadata, UppyUploadResult } from './types'

export default Vue.extend({
  name: 'Uploader',
  props: {
    itemNames: {
      type: Array as PropType<string[]>,
    },
  },
  computed: {
    ...mapGetters({
      path: 'file/path',
      s3Info: 'file/s3Info',
      theme: 'theme',
      uuid: 'file/selectedDriveUuid',
      profile: 'user/profile',
    }),
  },

  mounted() {
    const { getConsiderateName } = this

    const uppy = Uppy({
      onBeforeFileAdded: (currentFile, _files) => {
        trackDriveBrowserFilesUploadedAttempt(this.profile, this.uuid)
        const considerateName = getConsiderateName(currentFile.name)
        const name =
          this.path === '/' ? considerateName : `${this.path.slice(1)}/${considerateName}`
        const modifiedFile = {
          ...currentFile,
          name,
        }
        const meta: FileMetadata = {
          // NOTE: Please note that we are not setting name here
          // It goes directly to x-amz-meta-name that does not like Polish characters.
          // https://github.com/transloadit/uppy/issues/2380
          name: '',
          s3bucket: this.s3Info.bucket,
          s3key: this.s3Info.s3Key,
          s3secret: this.s3Info.s3Secret,
        }
        modifiedFile.meta = meta
        return modifiedFile
      },
      allowMultipleUploads: false,
      debug: true,
    })
      .use(Dashboard, {
        inline: true,
        target: '#uppy-uploader',
        showLinkToFileUploadResult: false,
        disableThumbnailGenerator: true,
        proudlyDisplayPoweredByUppy: false, // use modal
        allowMultipleUploads: false,
        showProgressDetails: true,
        height: 250,
        theme: this.theme,
      })
      .use(AwsS3Multipart, {
        limit: 4,
        companionUrl: uppyCompanionUrl,

        async createMultipartUpload(file) {
          const customMetadata = file.meta as FileMetadata
          const response = await fetch(`${uppyCompanionUrl}/s3/multipart`, {
            method: 'post',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              filename: getConsiderateName(file.name),
              type: file.type,
              metadata: {
                // ditto. see above comment on not setting name in metadata
                // x-amz-meta-name header cannot contain special characters.
                s3bucket: customMetadata.s3bucket,
                s3key: customMetadata.s3key,
                s3secret: customMetadata.s3secret,
              },
            }),
          })
          return response.json()
        },
      })
    uppy.on('upload', (data: UppyUploadResult) => {
      this.$emit('upload-start', data)
    })
    uppy.on('error', (error: Error) => {
      this.$emit('upload-error', error)
    })
    uppy.on('complete', async (result: UppyUploadResult) => {
      uppy.cancelAll()
      this.$emit('upload-complete', result)
    })
  },
  methods: {
    getConsiderateName(name: string): string {
      return indicateNth(name, this.itemNames)
    },
  },
})
